import { useEffect, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../../utils/auth";
import { GitCompare, Loader2, Play, Square, Trash2, Type } from "lucide-react";
import { PlaygroundProvider, usePlayground } from "../../contexts/PlaygroundContext";
import { API_BASE } from "../../shared/Constants";
import { PlaygroundMode } from "./types";
import ModelSelector from "./ModelSelector";
import PlaygroundInputArea from "./PlaygroundInputArea";
import PlaygroundParametersPanel from "./PlaygroundParametersPanel";
import PlaygroundResponseGrid from "./PlaygroundResponseGrid";
import PlaygroundSessionManager, { PlaygroundSessionManagerRef } from "./PlaygroundSessionManager";
import { usePlaygroundStreaming } from "../../hooks/usePlaygroundStreaming";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { reportApiError, reportHandledError } from "../../utils/reportError";

function PlaygroundContent() {
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const { state, dispatch, clearResponses, setMode } = usePlayground();
    const { generateResponses, stopGeneration } = usePlaygroundStreaming();
    const [isLoading, setIsLoading] = useState(true);
    const sessionManagerRef = useRef<PlaygroundSessionManagerRef>(null);

    const handleModeChange = (newMode: PlaygroundMode) => {
        if (!state.isGenerating && newMode !== state.mode) {
            sessionManagerRef.current?.clearSession();
            setMode(newMode);
        }
    };

    useEffect(() => {
        const fetchModels = async () => {
            try {
                setIsLoading(true);
                const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
                if (!token) return;
                const header = new Headers();
                header.append("Authorization", `Auth ${token}`);
                const response = await fetch(`${API_BASE}/api/chat/models`, {
                    method: "GET",
                    headers: header,
                });
                if (!response.ok) {
                    reportApiError(response, { feature: "playground.models", method: "GET" });
                    throw new Error(`Failed to fetch models: ${response.status}`);
                }
                const data = await response.json();
                const modelsData = data["models"] || {};
                const models = Object.keys(modelsData);
                const pricing: Record<string, { input_price: number; output_price: number }> = {};
                for (const modelName of models) {
                    const modelInfo = modelsData[modelName];
                    pricing[modelName] = {
                        input_price: modelInfo.input_price || 0,
                        output_price: modelInfo.output_price || 0,
                    };
                }
                dispatch({ type: 'SET_AVAILABLE_MODELS', models, pricing });
            } catch (error) {
                reportHandledError(error, { category: "api", feature: "playground.models" });
                console.error("Error fetching models:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchModels();
    }, [getAccessTokenSilently, loginWithRedirect, dispatch]);

    if (isLoading) {
        return (
            <div className="flex h-full flex-col items-center justify-center gap-4 py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Carregando modelos...</p>
            </div>
        );
    }

    const handleGenerate = async () => {
        if (state.selectedModels.length === 0) return;
        const hasContent = state.userMessages.some(msg => msg.trim().length > 0);
        if (!hasContent) return;
        await generateResponses();
    };

    const handleStop = () => stopGeneration();
    const handleClear = () => clearResponses();

    const hasPromptContent = state.userMessages.some(msg => msg.trim().length > 0);
    const canGenerate = state.selectedModels.length > 0 && hasPromptContent;
    const subtitle = state.mode === 'compare-models'
        ? 'Compare diferentes modelos com o mesmo prompt'
        : 'Compare diferentes prompts com o mesmo modelo';

    return (
        <div className="flex h-full w-full flex-col gap-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-3">
                        <h1 className="text-xl font-semibold tracking-tight text-foreground">Playground</h1>
                        <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5">
                            {([
                                { value: 'compare-models', label: 'Comparar Modelos', Icon: GitCompare },
                                { value: 'compare-prompts', label: 'Comparar Prompts', Icon: Type },
                            ] as const).map(({ value, label, Icon }) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => handleModeChange(value)}
                                    disabled={state.isGenerating}
                                    className={cn(
                                        "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                        state.mode === value
                                            ? "bg-background text-foreground shadow-[0_1px_2px_rgba(60,66,87,0.08)]"
                                            : "text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{subtitle}</p>
                    <p className="text-xs text-muted-foreground">
                        As chamadas consomem créditos da sua conta. Novos usuários recebem R$ 20,00 em créditos, que podem ser resgatados na aba Recarga.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <PlaygroundSessionManager ref={sessionManagerRef} disabled={state.isGenerating} />
                    <Button
                        variant="outline"
                        onClick={handleClear}
                        disabled={state.isGenerating || Object.keys(state.responses).length === 0}
                    >
                        <Trash2 className="h-4 w-4" />
                        Limpar
                    </Button>
                    {state.isGenerating ? (
                        <Button variant="destructive" onClick={handleStop}>
                            <Square className="h-4 w-4" />
                            Parar
                        </Button>
                    ) : (
                        <Button onClick={handleGenerate} disabled={!canGenerate}>
                            {state.isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            Gerar Respostas
                        </Button>
                    )}
                </div>
            </div>

            <ModelSelector />

            <div className="flex flex-1 flex-col gap-5 lg:flex-row min-h-0">
                <div className="flex flex-1 flex-col gap-5 min-w-0">
                    <PlaygroundInputArea />
                    <PlaygroundResponseGrid />
                </div>
                {state.isParametersPanelOpen && (
                    <div className="w-full flex-shrink-0 lg:w-[300px] order-first lg:order-none">
                        <PlaygroundParametersPanel />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function PlaygroundPage() {
    return (
        <PlaygroundProvider>
            <PlaygroundContent />
        </PlaygroundProvider>
    );
}
